import type {ReactElement} from 'react';
import React from 'react';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import type {RGBA} from '@Utils/color.util';
import {rgbaToHexString} from '@Utils/color.util';
import {useGetShadowOptionsObjectOfActiveSlideItem} from '@Hooks/poster-editor/useGetShadowOptionsObjectOfActiveSlideItem';
import {
  convertShadowOptionsBlurToFabricShadowBlur,
  getDefaultFabricShadowForType,
  getFabricShadowOffsetsFromShadowOptions,
  getFabricShadowOrGlowTypeFromShadowType,
  isCustomShadow,
  isShadow,
} from '@Utils/fabric-shadow.util';
import type {ShadowType} from '@/poster-whiteboard/poster/poster-backend.types';
import {BaseItemShadowOptions} from '@Components/poster-editor/components/base-item-shadow-options/base-item-shadow-options';

interface SlideshowItemShadowOptionProps {
  onClickColorFill(): void;
}

export function SlideshowItemShadowOption({...props}: SlideshowItemShadowOptionProps): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const activeSlideShadowOptions = useGetShadowOptionsObjectOfActiveSlideItem();

  if (!activeItem?.isSlideshow()) {
    throw new Error(`Expected slideshow item but got ${activeItem?.gitype}`);
  }

  if (!activeSlideUID || !activeSlide) {
    throw new Error('unable to get the selected text slide');
  }
  if (activeSlideShadowOptions === undefined) {
    throw new Error('selected slide item does not have a shadow property');
  }

  const onToggleShadow = (newShadowType: ShadowType | null): void => {
    const newType = getFabricShadowOrGlowTypeFromShadowType(newShadowType);
    const currentType = activeSlideShadowOptions?.shadowOrGlowType;

    const updatedShadow = getDefaultFabricShadowForType(newType, activeSlide.gitype, activeSlide.getScaleForShadow());
    if (!updatedShadow) {
      void activeItem.updateSlideFromObject(activeSlideUID, {shadow: null});

      return;
    }

    const shouldPreserveOffsets = isShadow(currentType) && isShadow(newType) && !isCustomShadow(currentType) && !isCustomShadow(newType);

    const {offsetX, offsetY, ...restShadow} = updatedShadow;

    const shadowToApply = shouldPreserveOffsets ? {...restShadow} : updatedShadow;

    void activeItem.updateSlideFromObject(activeSlideUID, {shadow: shadowToApply});
  };

  const updateOpacity = (dropShadowAlpha: number, undoable = true): void => {
    if (!activeSlideShadowOptions) {
      return;
    }

    const activeSlideDropShadowColor = activeSlideShadowOptions.color.slice() as RGBA;
    activeSlideDropShadowColor[3] = dropShadowAlpha;
    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        shadow: {
          color: rgbaToHexString(activeSlideDropShadowColor),
        },
      },
      {
        undoable,
      }
    );
  };

  const updateAngle = (dropShadowAngle: number, undoable = true): void => {
    if (!activeSlideShadowOptions) {
      return;
    }

    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        shadow: {
          ...getFabricShadowOffsetsFromShadowOptions({...activeSlideShadowOptions, angle: dropShadowAngle}, activeSlide.getScaleForShadow()),
        },
      },
      {
        undoable,
      }
    );
  };

  const updateDistance = (dropShadowDistance: number, angle: number, undoable = true): void => {
    if (!activeSlideShadowOptions) {
      return;
    }

    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        shadow: {
          ...getFabricShadowOffsetsFromShadowOptions({...activeSlideShadowOptions, distance: dropShadowDistance, angle}, activeSlide.getScaleForShadow()),
        },
      },
      {
        undoable,
      }
    );
  };

  const updateBlur = (dropShadowBlur: number, undoable = true): void => {
    if (!activeSlideShadowOptions) {
      return;
    }

    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        shadow: {
          blur: convertShadowOptionsBlurToFabricShadowBlur(dropShadowBlur, activeSlide.getScaleForShadow()),
        },
      },
      {
        undoable,
      }
    );
  };

  return (
    <BaseItemShadowOptions
      shadowOptions={activeSlideShadowOptions}
      itemType={activeSlide.gitype}
      onClickColorFill={(): void => {
        props.onClickColorFill();
      }}
      onToggleShadow={onToggleShadow}
      onUpdateOpacity={updateOpacity}
      onUpdateAngle={updateAngle}
      onUpdateDistance={updateDistance}
      onUpdateBlur={updateBlur}
    />
  );
}
